/**
 * Global Cart Handler for Shopify
 * Handles adding products to cart, updating cart count, and cart page navigation
 */

class CartHandler {
  constructor() {
    this.cartCountElements = document.querySelectorAll('.snipcart-items-count, .cart-count');
    this.cartDrawer = document.querySelector('.cart-drawer');
    this.cartPage = document.querySelector('.cart-page');
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
  }

  bindEvents() {
    // Handle add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) {
        e.preventDefault();
        const button = e.target.closest('[data-add-to-cart]');
        this.addToCart(button);
      }
    });

    // Handle cart count clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-count]')) {
        e.preventDefault();
        this.openCart();
      }
    });

    // Handle cart drawer close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) {
        this.closeCart();
      }
    });

    // Close cart drawer when clicking outside
    document.addEventListener('click', (e) => {
      if (this.cartDrawer && this.cartDrawer.classList.contains('open') && 
          !this.cartDrawer.contains(e.target) && 
          !e.target.closest('[data-cart-count]')) {
        this.closeCart();
      }
    });
  }

  async addToCart(button) {
    const productId = button.dataset.productId;
    const quantity = parseInt(button.dataset.quantity) || 1;
    const variantId = button.dataset.variantId;
    const properties = this.getProductProperties(button);

    if (!productId) {
      console.error('Product ID is required');
      return;
    }

    // Show loading state
    this.setButtonLoading(button, true);

    try {
      const formData = {
        'items': [{
          'id': variantId || productId,
          'quantity': quantity,
          'properties': properties
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        await this.updateCartCount();
        
        // Reset button state after success
        this.setButtonLoading(button, false);
        
        // Open cart drawer if enabled
        if (this.cartDrawer) {
          this.openCart();
        }
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage(button, error.message);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  getProductProperties(button) {
    const properties = {};
    const form = button.closest('form');
    
    if (form) {
      const inputs = form.querySelectorAll('input[name^="properties["]');
      inputs.forEach(input => {
        if (input.type === 'checkbox') {
          if (input.checked) {
            properties[input.name.match(/\[(.*?)\]/)[1]] = input.value;
          }
        } else if (input.type === 'radio') {
          if (input.checked) {
            properties[input.name.match(/\[(.*?)\]/)[1]] = input.value;
          }
        } else {
          properties[input.name.match(/\[(.*?)\]/)[1]] = input.value;
        }
      });
    }

    return properties;
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        
        // Update all cart count elements
        this.cartCountElements.forEach(element => {
          // Only update the text content, preserve the SVG icon
          if (element.tagName === 'SPAN') {
            element.textContent = cart.item_count;
          } else if (element.tagName === 'BUTTON') {
            // For buttons, find the span inside and update only that
            const spanElement = element.querySelector('.snipcart-items-count, .cart-count');
            if (spanElement) {
              spanElement.textContent = cart.item_count;
            }
          }
        });

        // Update cart drawer if it exists
        if (this.cartDrawer) {
          this.updateCartDrawer(cart);
        }

        // Dispatch custom event for other components
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart }
        }));
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  updateCartDrawer(cart) {
    const cartItems = this.cartDrawer.querySelector('.cart-items');
    const cartTotal = this.cartDrawer.querySelector('.cart-total');
    const cartCount = this.cartDrawer.querySelector('.cart-count');

    if (cartItems) {
      cartItems.innerHTML = '';
      cart.items.forEach(item => {
        const itemElement = this.createCartItemElement(item);
        cartItems.appendChild(itemElement);
      });
    }

    if (cartTotal) {
      cartTotal.textContent = this.formatMoney(cart.total_price);
    }

    if (cartCount) {
      cartCount.textContent = cart.item_count;
    }
  }

  createCartItemElement(item) {
    const div = document.createElement('div');
    div.className = 'cart-item';
    div.innerHTML = `
      <div class="cart-item-image">
        <img src="${item.image}" alt="${item.title}" width="60" height="60">
      </div>
      <div class="cart-item-details">
        <h4>${item.title}</h4>
        <p>${this.formatMoney(item.price)} x ${item.quantity}</p>
      </div>
      <button class="remove-item" data-key="${item.key}">×</button>
    `;
    return div;
  }

  openCart() {
    // If cart drawer exists, open it
    if (this.cartDrawer) {
      this.cartDrawer.classList.add('open');
      document.body.classList.add('cart-open');
    } else {
      // Otherwise, navigate to cart page (use localized route)
      const cartUrl = window.routes?.cart_url || '/cart';
      window.location.href = cartUrl;
    }
  }

  closeCart() {
    if (this.cartDrawer) {
      this.cartDrawer.classList.remove('open');
      document.body.classList.remove('cart-open');
    }
  }

  setButtonLoading(button, loading) {
    if (loading) {
      button.disabled = true;
      button.classList.add('loading');
    } else {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  showErrorMessage(button, message) {
    const originalText = button.dataset.originalText || button.textContent.trim();
    button.textContent = 'Error';
    button.classList.add('error');
    
    // Show error message to user
    this.showNotification(message, 'error');
    
    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('error');
    }, 3000);
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#dc2626' : '#10b981'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 1000;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }
}

// Initialize cart handler when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CartHandler();
});

// Export for use in other scripts
window.CartHandler = CartHandler;
